import { alpha, createTheme } from "@mui/material/styles";

export const PRIMARY_COLOR = "#00ABD0";
export const SECONDARY_COLOR = "#EB008B";
export const TERTIARY_GREEN_COLOR = "#00D08D";
export const TERTIARY_BLUE_COLOR = "#0043D0";
export const TERTIARY_YELLOW_COLOR = "#FFBF00";
export const NEUTRAL_WHITE = "#FFFFFF";
export const ERROR_COLOR = "#B3261E";

export const DEFAULT_FONT_FAMILY = "Manrope, Roboto";

// Notistack uses zIndex of 1400
export const LOADER_ZINDEX = 1399;
// Mui Modal uses zIndex of 1300
export const PRODUCT_TOUR_ZINDEX = 1300;
export const HIGHLIGHTED_TOOLTIP_ZINDEX = 1200;
// ECharts tooltip is overriden to be 1100

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    xsm: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
  }

  interface TypographyVariants {
    displayLarge: React.CSSProperties;
    displayMedium: React.CSSProperties;
    displaySmall: React.CSSProperties;
    headlineLarge: React.CSSProperties;
    headlineMedium: React.CSSProperties;
    headlineSmall: React.CSSProperties;
    titleLarge: React.CSSProperties;
    titleMedium: React.CSSProperties;
    titleSmall: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyMedium: React.CSSProperties;
    bodySmall: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    labelSmall: React.CSSProperties;
    labelXSmall: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    displayLarge?: React.CSSProperties;
    displayMedium?: React.CSSProperties;
    displaySmall?: React.CSSProperties;
    headlineLarge?: React.CSSProperties;
    headlineMedium?: React.CSSProperties;
    headlineSmall?: React.CSSProperties;
    titleLarge?: React.CSSProperties;
    titleMedium?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyMedium?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
    labelLarge?: React.CSSProperties;
    labelMedium?: React.CSSProperties;
    labelSmall?: React.CSSProperties;
    labelXSmall?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    displayLarge: true;
    displayMedium: true;
    displaySmall: true;
    headlineLarge: true;
    headlineMedium: true;
    headlineSmall: true;
    titleLarge: true;
    titleMedium: true;
    titleSmall: true;
    bodyLarge: true;
    bodyMedium: true;
    bodySmall: true;
    labelLarge: true;
    labelMedium: true;
    labelSmall: true;
    labelXSmall: true;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    onPrimary: Palette["primary"];
    onSecondary: Palette["primary"];
    tertiaryGreen: Palette["primary"];
    tertiaryBlue: Palette["primary"];
    tertiaryDarkBlue: Palette["primary"];
    tertiaryYellow: Palette["primary"];
    neutralWhite: Palette["primary"];
    neutralGray: Palette["primary"];
    surface: Palette["primary"];
    surfaceVariant: Palette["primary"];
    surfaceContainer: Palette["primary"];
    onSurface: Palette["primary"];
    onSurfaceVariant: Palette["primary"];
    outline: Palette["primary"];
    outlineVariant: Palette["primary"];
  }
  interface PaletteOptions {
    onPrimary: PaletteOptions["primary"];
    onSecondary: PaletteOptions["primary"];
    tertiaryGreen: PaletteOptions["primary"];
    tertiaryBlue: PaletteOptions["primary"];
    tertiaryDarkBlue: PaletteOptions["primary"];
    tertiaryYellow: PaletteOptions["primary"];
    neutralWhite: PaletteOptions["primary"];
    neutralGray: PaletteOptions["primary"];
    surface: PaletteOptions["primary"];
    surfaceVariant: PaletteOptions["primary"];
    surfaceContainer: PaletteOptions["primary"];
    onSurface: PaletteOptions["primary"];
    onSurfaceVariant: PaletteOptions["primary"];
    outline: PaletteOptions["primary"];
    outlineVariant: PaletteOptions["primary"];
  }
}

declare module "@mui/material/ToggleButtonGroup" {
  interface ToggleButtonGroupPropsColorOverrides {
    outline: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    tertiaryGreen: true;
    neutralWhite: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    tertiaryGreen: true;
    tertiaryBlue: true;
    neutralGray: true;
    tertiaryDarkBlue: true;
    outline: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    onSurface: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    neutralWhite: true;
  }
}

declare module "@mui/material/LinearProgress" {
  interface LinearProgressPropsColorOverrides {
    tertiaryGreen: true;
  }
}

export default createTheme({
  breakpoints: {
    values: {
      xs: 0,
      xsm: 400,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      main: PRIMARY_COLOR,
      contrastText: NEUTRAL_WHITE,
      light: alpha(PRIMARY_COLOR, 0.1),
    },
    onPrimary: {
      main: NEUTRAL_WHITE,
    },
    onSecondary: {
      main: NEUTRAL_WHITE,
    },
    secondary: {
      main: SECONDARY_COLOR,
      contrastText: NEUTRAL_WHITE,
      light: alpha(SECONDARY_COLOR, 0.1),
    },
    tertiaryGreen: {
      main: TERTIARY_GREEN_COLOR,
      contrastText: NEUTRAL_WHITE,
      light: alpha(TERTIARY_GREEN_COLOR, 0.1),
    },
    tertiaryBlue: {
      main: TERTIARY_BLUE_COLOR,
      contrastText: NEUTRAL_WHITE,
      light: alpha(TERTIARY_BLUE_COLOR, 0.1),
    },
    tertiaryDarkBlue: {
      main: "#003E59",
      contrastText: NEUTRAL_WHITE,
      light: alpha("#003E59", 0.1),
    },
    tertiaryYellow: {
      main: TERTIARY_YELLOW_COLOR,
      light: alpha(TERTIARY_YELLOW_COLOR, 0.1),
    },
    neutralGray: {
      main: "#F2F3F4",
      contrastText: NEUTRAL_WHITE,

      light: alpha("#F2F3F4", 0.75),
    },
    neutralWhite: {
      main: NEUTRAL_WHITE,
    },
    error: {
      main: ERROR_COLOR,
    },
    surface: {
      dark: "#DED8E1",
      main: "#FEF7FF",
    },
    surfaceVariant: {
      main: "#E7E0EC",
      light: alpha("#E7E0EC", 0.6),
    },
    surfaceContainer: {
      dark: "#F7F2FA",
      main: "#F3EDF7",
      light: NEUTRAL_WHITE,
    },
    onSurface: {
      main: "#1D1B20",
    },
    onSurfaceVariant: {
      main: "#49454F",
    },
    outline: {
      main: "#79747E",
      contrastText: NEUTRAL_WHITE,
    },
    outlineVariant: {
      main: "#CAC4D0",
    },
    background: {
      default: "#F7F2FA",
    },
  },
  typography: {
    fontFamily: DEFAULT_FONT_FAMILY,
    displayLarge: {
      fontWeight: 400,
      fontSize: "3.563rem",
    },
    displayMedium: {
      fontWeight: 400,
      fontSize: "2.813rem",
    },
    displaySmall: {
      fontWeight: 400,
      fontSize: "2.25rem",
    },
    headlineLarge: {
      fontWeight: 400,
      fontSize: "2rem",
    },
    headlineMedium: {
      fontWeight: 400,
      fontSize: "1.75rem",
    },
    headlineSmall: {
      fontWeight: 400,
      fontSize: "1.5rem",
    },
    titleLarge: {
      fontWeight: 400,
      fontSize: "1.375rem",
    },
    titleMedium: {
      fontWeight: 500,
      fontSize: "1rem",
    },
    titleSmall: {
      fontWeight: 500,
      fontSize: "0.875rem",
    },
    bodyLarge: {
      fontWeight: 400,
      fontSize: "1rem",
    },
    bodyMedium: {
      fontWeight: 400,
      fontSize: "0.875rem",
    },
    bodySmall: {
      fontWeight: 400,
      fontSize: "0.75rem",
    },
    labelLarge: {
      fontWeight: 500,
      fontSize: "0.875rem",
    },
    labelMedium: {
      fontWeight: 500,
      fontSize: "0.75rem",
    },
    labelSmall: {
      fontWeight: 500,
      fontSize: "0.688rem",
    },
    labelXSmall: {
      fontWeight: 500,
      fontSize: "0.625rem",
    },
  },
  components: {
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.surfaceVariant.main,
          ":hover": {
            backgroundColor: theme.palette.surfaceVariant.light,
          },
          ":focus": {
            backgroundColor: theme.palette.surfaceVariant.light,
          },
          ":focus-within": {
            backgroundColor: theme.palette.surfaceVariant.light,
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.onSurfaceVariant.main,
          fontWeight: theme.typography.titleMedium.fontWeight,
          fontSize: theme.typography.titleMedium.fontSize,
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.outline.main,
          color: theme.palette.onSurface.main,
          fontWeight: theme.typography.labelLarge.fontWeight,
          fontSize: theme.typography.labelLarge.fontSize,
          textTransform: "capitalize",
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: theme.typography.labelLarge.fontWeight,
          fontSize: theme.typography.labelLarge.fontSize,
          textTransform: "capitalize",
          borderRadius: "6.25rem",
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          displayLarge: "h1",
          displayMedium: "h2",
          displaySmall: "h3",
          headlineLarge: "h2",
          headlineMedium: "h3",
          headlineSmall: "h4",
          titleLarge: "h3",
          titleMedium: "h4",
          titleSmall: "div",
          bodyLarge: "p",
          bodyMedium: "p",
          bodySmall: "p",
          labelLarge: "span",
          labelMedium: "span",
          labelSmall: "span",
          labelXSmall: "span",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.outlineVariant.main,
          borderRadius: "0.75rem",
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.surfaceContainer.light,
          [theme.breakpoints.up("md")]: {
            marginBottom: "2rem",
          },
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.surfaceVariant.main,
        }),
      },
    },
  },
});
